import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import { AlertTriangle, Shield, Activity, AlertCircle } from 'lucide-react';

type RiskMetric = {
  id: string;
  master: string;
  type: 'copy_trading' | 'pamm' | 'mam';
  drawdown: number;
  maxDrawdown: number;
  exposure: number;
  marginLevel: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  status: 'normal' | 'warning' | 'danger';
  lastUpdated: string;
};

const AdminRiskMonitoring = () => {
  const [riskFilter, setRiskFilter] = useState<
    'all' | 'low' | 'medium' | 'high' | 'critical'
  >('all');

  const riskMetrics: RiskMetric[] = [
    {
      id: 'RISK001',
      master: 'John Smith',
      type: 'copy_trading',
      drawdown: 8.5,
      maxDrawdown: 15.2,
      exposure: 450000,
      marginLevel: 250,
      riskLevel: 'medium',
      status: 'normal',
      lastUpdated: '2024-01-15 10:30:00',
    },
    {
      id: 'RISK002',
      master: 'Mike Johnson',
      type: 'pamm',
      drawdown: 18.3,
      maxDrawdown: 22.5,
      exposure: 890000,
      marginLevel: 120,
      riskLevel: 'high',
      status: 'warning',
      lastUpdated: '2024-01-15 09:45:00',
    },
    {
      id: 'RISK003',
      master: 'Sarah Williams',
      type: 'copy_trading',
      drawdown: 5.2,
      maxDrawdown: 10.8,
      exposure: 320000,
      marginLevel: 450,
      riskLevel: 'low',
      status: 'normal',
      lastUpdated: '2024-01-15 08:20:00',
    },
    {
      id: 'RISK004',
      master: 'David Brown',
      type: 'mam',
      drawdown: 25.7,
      maxDrawdown: 30.0,
      exposure: 1200000,
      marginLevel: 85,
      riskLevel: 'critical',
      status: 'danger',
      lastUpdated: '2024-01-15 07:55:00',
    },
    {
      id: 'RISK005',
      master: 'Emily Chen',
      type: 'pamm',
      drawdown: 12.4,
      maxDrawdown: 18.0,
      exposure: 560000,
      marginLevel: 180,
      riskLevel: 'medium',
      status: 'normal',
      lastUpdated: '2024-01-15 07:30:00',
    },
  ];

  const filteredMetrics = riskMetrics.filter(
    metric => riskFilter === 'all' || metric.riskLevel === riskFilter
  );

  const riskColumns: DataTableColumn<RiskMetric>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '100px',
    },
    {
      id: 'master',
      header: 'Master Account',
      accessor: row => (
        <div>
          <div className="font-semibold">{row.master}</div>
          <div className="text-sm text-muted-foreground">
            {row.type.replace('_', ' ').toUpperCase()}
          </div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.master,
      width: '180px',
    },
    {
      id: 'drawdown',
      header: 'Drawdown',
      accessor: row => (
        <div>
          <div
            className={`font-semibold ${row.drawdown > 15 ? 'text-red-600' : row.drawdown > 10 ? 'text-yellow-600' : 'text-green-600'}`}
          >
            {row.drawdown}%
          </div>
          <div className="text-xs text-muted-foreground">
            Max: {row.maxDrawdown}%
          </div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.drawdown,
      width: '120px',
    },
    {
      id: 'exposure',
      header: 'Exposure',
      accessor: row => `$${(row.exposure / 1000).toFixed(0)}K`,
      sortable: true,
      sortValue: row => row.exposure,
      width: '100px',
    },
    {
      id: 'marginLevel',
      header: 'Margin Level',
      accessor: row => (
        <span
          className={`font-semibold ${row.marginLevel < 100 ? 'text-red-600' : row.marginLevel < 150 ? 'text-yellow-600' : 'text-green-600'}`}
        >
          {row.marginLevel}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.marginLevel,
      width: '120px',
    },
    {
      id: 'riskLevel',
      header: 'Risk Level',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.riskLevel === 'low'
              ? 'bg-green-100 text-green-800'
              : row.riskLevel === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : row.riskLevel === 'high'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-red-100 text-red-800'
          }`}
        >
          {row.riskLevel.toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.riskLevel,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'normal'
              ? 'bg-green-100 text-green-800'
              : row.status === 'warning'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '100px',
    },
    {
      id: 'lastUpdated',
      header: 'Last Updated',
      accessor: row => new Date(row.lastUpdated).toLocaleString(),
      sortable: true,
      sortValue: row => row.lastUpdated,
      width: '180px',
    },
  ];

  const criticalCount = riskMetrics.filter(
    m => m.riskLevel === 'critical'
  ).length;
  const highCount = riskMetrics.filter(m => m.riskLevel === 'high').length;
  const mediumCount = riskMetrics.filter(m => m.riskLevel === 'medium').length;
  const totalExposure = riskMetrics.reduce((sum, m) => sum + m.exposure, 0);

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Risk Monitoring"
          description="Monitor risk levels across all trading services"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={riskFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setRiskFilter('all')}
        >
          All Risk Levels
        </Button>
        <Button
          variant={riskFilter === 'low' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setRiskFilter('low')}
        >
          Low
        </Button>
        <Button
          variant={riskFilter === 'medium' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setRiskFilter('medium')}
        >
          Medium
        </Button>
        <Button
          variant={riskFilter === 'high' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setRiskFilter('high')}
        >
          High
        </Button>
        <Button
          variant={riskFilter === 'critical' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setRiskFilter('critical')}
        >
          Critical
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Critical Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              {criticalCount}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Immediate attention required
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              High Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {highCount}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Monitor closely
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-yellow-600" />
              Medium Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">
              {mediumCount}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Normal monitoring
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Total Exposure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${(totalExposure / 1000000).toFixed(2)}M
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Across all masters
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Risk Metrics Dashboard</CardTitle>
          <CardDescription>
            Real-time risk monitoring for all master accounts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={riskColumns}
            data={filteredMetrics}
            searchPlaceholder="Search accounts..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRiskMonitoring;
